/** Safe schema.org JSON-LD — only facts shown on public pages. No ratings or affiliations. */

export const SITE = "https://www.nexorahai.com";
export const ORG_ID = `${SITE}/#organization`;
export const WEBSITE_ID = `${SITE}/#website`;
export const COURSE_ID = `${SITE}/#cphq-course`;

const LOGO = `${SITE}/assets/nexorah-logo.png`;

/** Matches Contact page (Contact.jsx). */
export const ORGANIZATION = {
  "@type": "Organization",
  "@id": ORG_ID,
  name: "Nexorah",
  alternateName: "Nexorah AI",
  url: SITE,
  logo: LOGO,
  email: "admin@nexorahai.com",
  telephone: "+1-713-399-0222",
  address: {
    "@type": "PostalAddress",
    streetAddress: "12645 Memorial Dr, Suite F1 #1221",
    addressLocality: "Houston",
    addressRegion: "TX",
    postalCode: "77024",
    addressCountry: "US",
  },
  contactPoint: [
    {
      "@type": "ContactPoint",
      contactType: "customer support",
      email: "admin@nexorahai.com",
      telephone: "+1-713-399-0222",
      availableLanguage: ["English"],
    },
  ],
};

/** Visible on homepage hero (LandingPage.jsx). */
export const CPHQ_COURSE = {
  "@type": "Course",
  "@id": COURSE_ID,
  name: "CPHQ Exam Preparation",
  description:
    "Online preparation for the Certified Professional in Healthcare Quality (CPHQ) examination with structured modules, exam-style practice, mock exams, and AI-guided study support.",
  url: `${SITE}/`,
  provider: { "@id": ORG_ID },
  inLanguage: "en",
};

export function buildWebSite(description) {
  return {
    "@type": "WebSite",
    "@id": WEBSITE_ID,
    name: "Nexorah",
    url: `${SITE}/`,
    description,
    publisher: { "@id": ORG_ID },
  };
}

function buildBreadcrumbList(pathname, headline) {
  return {
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: `${SITE}/`,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Learning Hub",
        item: `${SITE}/learning-hub`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: headline,
        item: `${SITE}${pathname}`,
      },
    ],
  };
}

function buildArticleNode(article, pathname) {
  const url = `${SITE}${pathname}`;
  const headline = article.seo_title_resolved || article.title || "";
  const description = article.seo_description_resolved || article.summary || "";
  const image = article.og_image_url || article.cover_image_url || LOGO;
  const authorName = article.author_name || "Nexorah AI";

  return {
    "@type": "Article",
    headline,
    description,
    image,
    author: {
      "@type": organizationOrPerson(authorName),
      name: authorName,
      ...(authorName.includes("Nexorah") ? { url: SITE } : {}),
    },
    publisher: { "@id": ORG_ID },
    mainEntityOfPage: url,
    url,
    inLanguage: "en",
    ...(article.published_at
      ? { datePublished: article.published_at }
      : {}),
    ...(article.updated_at ? { dateModified: article.updated_at } : {}),
  };
}

function organizationOrPerson(name) {
  return name.includes("Nexorah") ? "Organization" : "Person";
}

/**
 * @param {string} pathname
 * @param {string} pageDescription — from page meta
 * @param {object|null} article — Learning Hub article payload from API
 */
export function buildStructuredDataGraph(pathname, pageDescription, article = null) {
  const graph = [ORGANIZATION, buildWebSite(pageDescription || "")];

  if (pathname === "/") {
    graph.push(CPHQ_COURSE);
  }

  if (pathname === "/learning-hub") {
    graph.push({
      "@type": "CollectionPage",
      name: "CPHQ Articles & Study Guides",
      description:
        pageDescription ||
        "Public CPHQ certification articles and study guides from Nexorah AI.",
      url: `${SITE}/learning-hub`,
      isPartOf: { "@id": WEBSITE_ID },
      inLanguage: "en",
    });
  }

  if (article && pathname.startsWith("/learning-hub/")) {
    const headline = article.title || article.seo_title_resolved || "Article";
    graph.push(buildArticleNode(article, pathname));
    graph.push(buildBreadcrumbList(pathname, headline));
  }

  return {
    "@context": "https://schema.org",
    "@graph": graph,
  };
}

const SCRIPT_ID = "nexorah-structured-data";

const INDEXABLE_PREFIXES = [
  "/",
  "/learning-hub",
  "/sign-in",
  "/sign-up",
  "/contact",
  "/terms",
  "/privacy",
];

export function isIndexablePublicPath(pathname) {
  if (INDEXABLE_PREFIXES.includes(pathname)) return true;
  return /^\/learning-hub\/[^/]+$/.test(pathname);
}

/**
 * @param {string} pathname
 * @param {string} pageDescription
 * @param {object|null} article
 */
export function applyStructuredData(pathname, pageDescription, article = null) {
  const existing = document.getElementById(SCRIPT_ID);
  if (existing) existing.remove();

  if (!isIndexablePublicPath(pathname)) return;

  const script = document.createElement("script");
  script.id = SCRIPT_ID;
  script.type = "application/ld+json";
  script.text = JSON.stringify(buildStructuredDataGraph(pathname, pageDescription, article));
  document.head.appendChild(script);
}
